import { Alert, Button, Collapse } from '@mui/material';
import { ReactElement, useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { API_URLS } from '../../routes/urls';
import { ResendActivationResponseData, ResentActivationRequestData } from './types';

interface RenderPropsParams {
  loading: boolean;
  resetActivationResult: () => void;
  resendActivation: () => Promise<void>;
  renderMessage: (shouldRender?: boolean) => ReactElement;
  isAccountAlreadyActivated: boolean;
}
interface ResendActivationProviderProps {
  children: (renderProps: RenderPropsParams) => ReactElement;
  email?: string;
  showResendActivationButton?: boolean;
}

export const ResendActivationProvider: React.FC<ResendActivationProviderProps> = ({
  children,
  email,
  showResendActivationButton = true,
}) => {
  const [isAccountAlreadyActivated, setIsAccountAlreadyActivated] = useState(false);
  /**
   * Query to track the state of resending activation link
   */
  const { runQuery, loading, result, resetResult, error, clearErrors } = useQuery<
    ResendActivationResponseData,
    ResentActivationRequestData
  >(
    {
      url: API_URLS.resendActivationLink,
      method: 'POST',
    },
    {
      removeDataOnRequery: false,
      onSuccess(result) {
        if (result.success) {
          setIsAccountAlreadyActivated(false);
        } else {
          setIsAccountAlreadyActivated(true);
        }
      },
    }
  );

  /**
   * Function that sends activation link
   */
  const resendActivationLink = async () => {
    if (email) {
      await runQuery({
        email,
      });
    }
  };

  const renderMessage = (shouldRender = true): ReactElement => {
    /**
     * In case where resend activation link is successful
     */
    const showResendActivationLink = Boolean(result) && result?.success && shouldRender;

    /**
     * In case where account is already activated
     */
    const showAccountActivated = Boolean(result) && isAccountAlreadyActivated && shouldRender;

    /**
     * In case of errors
     */
    const showError = Boolean(error) && shouldRender;

    /**
     * UI to render all types of messages
     */
    return (
      <>
        <Collapse in={showResendActivationLink}>
          <Alert onClose={resetResult} severity="success" sx={{ mb: 2.5 }}>
            {result?.data.message}
            {result?.success && showResendActivationButton && (
              <Button onClick={resendActivationLink} variant="link" sx={{ ml: 0.5, height: 'auto' }}>
                {loading ? 'Resending link...' : 'Resend activation link'}
              </Button>
            )}
          </Alert>
        </Collapse>
        <Collapse in={showAccountActivated}>
          <Alert onClose={resetResult} severity="error" sx={{ mb: 2.5 }}>
            {result?.data.message}
          </Alert>
        </Collapse>
        <Collapse in={showError}>
          <Alert onClose={clearErrors} severity="error" sx={{ mb: 2.5 }}>
            {error?.message}
          </Alert>
        </Collapse>
      </>
    );
  };

  return children({
    loading,
    renderMessage,
    resendActivation: resendActivationLink,
    resetActivationResult: resetResult,
    isAccountAlreadyActivated,
  });
};
